/**
 * Battery Health page: plant-level health, capacity trend, warranty context.
 * Route: /app/battery-health/:plantId
 * Authority: docs/implementation/BATTERY_HEALTH_TRACKING.md
 */
import { useParams, Link } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { useBatteryHealth } from "@/hooks/useBatteryHealth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Battery, AlertTriangle, ArrowLeft, FileDown, FileSpreadsheet } from "lucide-react";
import { downloadWarrantyReportCsv, downloadWarrantyReportPdf } from "@/lib/warranty-report";
import type { WarrantyReportSnapshot } from "@/lib/warranty-report";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

export default function BatteryHealth() {
  const { plantId } = useParams<{ plantId: string }>();
  const { properties, plants } = useApp();
  const {
    plant,
    snapshot,
    trendSnapshots,
    activeAlerts,
    healthScore,
    warranty,
    thresholdKwh,
    ageYears,
    loading,
    error,
  } = useBatteryHealth(plantId ?? undefined);

  const propertyId = plant?.property_id;
  const property = properties.find((p) => p.id === propertyId);
  const plantDisplayName = plant?.display_name ?? plants.find((p) => p.id === plantId)?.display_name ?? "Battery Health";
  const installDate = plant?.install_date ? new Date(plant.install_date) : null;

  if (error) {
    return (
      <div className="p-6">
        <p className="text-destructive">Failed to load battery health data.</p>
        <Link to={propertyId ? `/app/property/${propertyId}` : "/app"} className="text-primary underline mt-2 inline-block">
          Back to {propertyId ? "Property" : "Dashboard"}
        </Link>
      </div>
    );
  }

  const chartData = trendSnapshots.map((s) => ({
    date: s.snapshot_date,
    computed: s.computed_capacity_kwh ?? 0,
    configured: s.configured_capacity_kwh,
    threshold: thresholdKwh ?? 0,
  }));

  function snapshotToReport(s: typeof trendSnapshots[0]): WarrantyReportSnapshot {
    return {
      snapshot_date: s.snapshot_date,
      configured_capacity_kwh: s.configured_capacity_kwh,
      computed_capacity_kwh: s.computed_capacity_kwh,
      usable_capacity_kwh: s.usable_capacity_kwh,
      total_degradation_percent: s.total_degradation_percent,
      cycles_counted: s.cycles_counted,
      estimated_historical_cycles: s.estimated_historical_cycles,
      estimated_total_cycles: s.estimated_total_cycles,
      soc_min: s.soc_min,
      soc_max: s.soc_max,
      charge_kwh: s.charge_kwh,
      discharge_kwh: s.discharge_kwh,
    };
  }

  function buildWarrantyReportInput() {
    if (!plantDisplayName) return null;
    return {
      plantDisplayName,
      snapshot: snapshot ? snapshotToReport(snapshot) : null,
      trendSnapshots: trendSnapshots.map(snapshotToReport),
      warranty: warranty ?? null,
      healthScore: healthScore ?? null,
      generatedAt: new Date().toISOString().replace("T", " ").slice(0, 19),
    };
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/app/properties" className="hover:text-foreground">Properties</Link>
        <span>/</span>
        {propertyId ? (
          <>
            <Link to={`/app/property/${propertyId}`} className="hover:text-foreground">{property?.name ?? "Property"}</Link>
            <span>/</span>
          </>
        ) : null}
        <span className="text-foreground">Battery Health</span>
      </div>

      <div className="flex items-center gap-3">
        <Link
          to={propertyId ? `/app/property/${propertyId}` : "/app"}
          className="p-1 rounded hover:bg-muted"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex items-center gap-2">
          <Battery className="h-6 w-6 text-muted-foreground" />
          <h1 className="text-xl font-semibold">{plantDisplayName} — Battery Health</h1>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Current Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {healthScore && (
                <div className="flex items-center gap-4 flex-wrap">
                  <div>
                    <span className="text-sm text-muted-foreground">Health Score</span>
                    <p className="text-2xl font-mono font-bold">
                      {healthScore.score}/100 <span className="text-lg font-normal">({healthScore.grade})</span>
                    </p>
                  </div>
                  <Badge
                    variant={healthScore.status === "critical" ? "destructive" : healthScore.status === "degrading" ? "default" : "secondary"}
                  >
                    {healthScore.status}
                  </Badge>
                  <p className="text-sm text-muted-foreground max-w-md">{healthScore.summary}</p>
                </div>
              )}
              {snapshot && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground block">Configured</span>
                    <span className="font-mono">{snapshot.configured_capacity_kwh?.toFixed(1) ?? "—"} kWh</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block">Current (computed)</span>
                    <span className="font-mono">
                      {snapshot.computed_capacity_kwh != null ? `${snapshot.computed_capacity_kwh.toFixed(1)} kWh` : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block">Degradation</span>
                    <span className="font-mono">
                      {snapshot.total_degradation_percent != null ? `${snapshot.total_degradation_percent.toFixed(1)}%` : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block">Cycles (counted)</span>
                    <span className="font-mono">{snapshot.cycles_counted ?? 0}</span>
                  </div>
                </div>
              )}
              {ageYears != null && (
                <p className="text-sm text-muted-foreground">
                  System age: ~{ageYears.toFixed(1)} years
                </p>
              )}
              {plant && !installDate && (
                <p className="text-sm text-muted-foreground">Install date unknown — enter in plant settings for age-based metrics.</p>
              )}
              {!snapshot && !healthScore && !loading && (
                <p className="text-sm text-muted-foreground">
                  No health data yet. Snapshots are generated daily (1:00 AM). Run backfill for historical data.
                </p>
              )}
            </CardContent>
          </Card>

          {chartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Capacity Trend</CardTitle>
                <p className="text-sm text-muted-foreground">Computed capacity over time (kWh)</p>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} unit=" kWh" />
                      <Tooltip formatter={(value: number) => [value?.toFixed(1), "kWh"]} labelFormatter={(l) => `Date: ${l}`} />
                      {thresholdKwh != null && thresholdKwh > 0 && (
                        <ReferenceLine y={thresholdKwh} stroke="hsl(var(--destructive))" strokeDasharray="4 4" />
                      )}
                      <Line type="monotone" dataKey="computed" stroke="hsl(var(--brand-accent))" strokeWidth={2} dot={false} name="Computed" />
                      <Line type="monotone" dataKey="configured" stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" dot={false} name="Configured" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {activeAlerts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Active Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {activeAlerts.map((a) => (
                    <li key={a.id} className="text-sm flex items-start gap-2">
                      <Badge variant={a.severity === "critical" ? "destructive" : "secondary"}>{a.severity}</Badge>
                      <span>{a.message}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {warranty && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Warranty</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {warranty.warrantyYears} years, {warranty.warrantyCycles} cycles, {warranty.warrantyCapacityPercent}% capacity threshold
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Generate a warranty report (PDF) or export snapshot data (CSV) for documentation. Reports include a disclaimer about estimated values.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      const input = buildWarrantyReportInput();
                      if (input) downloadWarrantyReportPdf(input);
                    }}
                    disabled={!snapshot && trendSnapshots.length === 0}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    Generate Warranty Report (PDF)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const input = buildWarrantyReportInput();
                      if (input) downloadWarrantyReportCsv(input);
                    }}
                    disabled={trendSnapshots.length === 0}
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Export Data (CSV)
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <p className="text-xs text-muted-foreground">
        Historical cycle counts before monitoring start are estimated from manufacturer curve. See docs/implementation/BATTERY_HEALTH_TRACKING.md.
      </p>
    </div>
  );
}
